import { Box } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import {
  DataGrid,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  type GridRowId,
  type GridRowModesModel,
  type GridRowsProp
} from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import type { TipoGanado } from '../../stores/tipos-ganado/tipo-ganado';
import transformToOptions from '../../utils/transform-to-options';
import ActionsCell from '../ui/ActionsCell';
import ComboBoxCell from '../ui/ComboBoxCell';
import HistorialCell from '../ui/HistorialCell';
import type { AnimalPreCompraRow } from './AnimalPreCompraRow';
import { colDefinitions } from './column-definitions';

interface AnimalesPreCompraProps {
  rows: GridRowsProp<AnimalPreCompraRow>;
  rowModesModel: GridRowModesModel;
  onRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
  tiposGanado: Array<TipoGanado>;
  esAgregar?: boolean;
  readonly: boolean;
  onClickHistorialSiiniga: (folio: string, partida: number) => void;
  onClickHistorialTemporal: (folio: string, partida: number) => void;
  onCancel: (id: GridRowId) => void;
  onDelete: (id: GridRowId) => void;
  onEdit: (id: GridRowId) => void;
  onSave: (id: GridRowId) => void;
  onRowUpdate: (
    newRow: AnimalPreCompraRow,
    oldRow: AnimalPreCompraRow
  ) => AnimalPreCompraRow | Promise<AnimalPreCompraRow>;
}

const getStyles = (theme: Theme) => ({
  dataGridContainer: {
    height: 280,
    maxWidth: '95vw'
  },
  dataGrid: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-iconSeparator': {
      borderRight: `1px solid ${theme.palette.primary.main}`
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold'
    },
    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
      borderBottom: `1px solid ${theme.palette.primary.main}`
    }
  }
});

const AnimalesPreCompra = ({
  rows,
  rowModesModel,
  readonly,
  esAgregar,
  onRowModesModelChange,
  tiposGanado,
  onClickHistorialSiiniga,
  onClickHistorialTemporal,
  onCancel,
  onDelete,
  onEdit,
  onSave,
  onRowUpdate
}: AnimalesPreCompraProps) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const [columnDefs, setColumnDefs] = useState(colDefinitions);
  const tiposGanadoOptions = transformToOptions(tiposGanado, { labelProp: 'descripcion' });

  const renderHistorialSiinigaCell = (params: GridRenderCellParams) => {
    return <HistorialCell {...params} onClick={onClickHistorialSiiniga} />;
  };

  const renderHistorialTemporalCell = (params: GridRenderCellParams) => {
    return <HistorialCell {...params} onClick={onClickHistorialTemporal} />;
  };

  const renderTipoGanadoEditCell = (params: GridRenderEditCellParams) => {
    return <ComboBoxCell {...params} options={tiposGanadoOptions} />;
  };
  const tipoGanadoValueFormatter = (value?: number) => {
    return value ? tiposGanado.find((t) => t.id === value)?.descripcion : '';
  };

  const renderActionsCell = (params: GridRenderCellParams) => {
    return (
      <ActionsCell
        {...params}
        onCancel={onCancel}
        onDelete={onDelete}
        onEdit={onEdit}
        onSave={onSave}
      />
    );
  };

  useEffect(() => {
    setColumnDefs((current) =>
      current.map((col) => {
        switch (col.field) {
          case 'historialSiiniga':
            return {
              ...col,
              renderCell: renderHistorialSiinigaCell
            };
          case 'historialTemporal':
            return {
              ...col,
              renderCell: renderHistorialTemporalCell
            };
          case 'idTipoGanado':
            return {
              ...col,
              valueFormatter: tipoGanadoValueFormatter,
              renderEditCell: renderTipoGanadoEditCell
            };
          case 'actions':
            return {
              ...col,
              renderCell: renderActionsCell
            };
          default:
            return col;
        }
      })
    );
  }, [tiposGanado]);

  const columnsVisibility = (noEditable: boolean, esAgregar: boolean) => ({
    historialSiiniga: !esAgregar,
    historialTemporal: !esAgregar,
    actions: !noEditable
  });

  return (
    <Box sx={classes.dataGridContainer}>
      <DataGrid
        sx={classes.dataGrid}
        rows={rows}
        columns={columnDefs}
        editMode={'row'}
        isCellEditable={(params) => (readonly ? false : params.colDef.editable === true)}
        columnVisibilityModel={columnsVisibility(readonly, !!esAgregar)}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        processRowUpdate={onRowUpdate}
        autoHeight={false}
        disableColumnMenu={true}
        disableColumnSorting={true}
        disableRowSelectionOnClick={true}
        disableColumnResize={true}
      />
    </Box>
  );
};

export default React.memo(AnimalesPreCompra);
